import Blockly from 'blockly/browser'
import Util, { defineBlock, mapSireniaPrimitiveToBlocklyPrimitive } from '../Util'
import _ from 'lodash'

Blockly.Extensions.registerMutator(
  'sirenia_api_flow_arg_mutator',
  {
    mutationToDom() {
      // Serialize mutator info to xml
      if (this.args == null || this.args.length === 0) {
        return null
      }
      let container = document.createElement('mutation')
      this.args.forEach((arg, idx) => {
        container.setAttribute('arg' + idx, `${arg.name}|${arg.type}`)
      })
      return container
    },
    domToMutation(container) {
      // Restore mutator info from xml
      this.args = []
      for (let i = 0; ; i++) {
        let argString = container.getAttribute(`arg${i}`)
        if (!argString) break
        const match = /^(.*)\|(.*)$/.exec(argString)
        if (match) {
          this.args[i] = {
            name: match[1],
            type: match[2]
          }
        }
      }
      this.updateShape_()
    },
    updateShape_() {
      for (let i = 0; i < this.args.length; i++) {
        this.appendValueInput('ARG' + i)
          .appendField(this.args[i].name)
          .setAlign(Blockly.ALIGN_RIGHT)
          .setCheck(this.args[i].type)
      }
    }
  },
  function() {
    this.args = this.args || []
  }
)

defineBlock(
  'sirenia_api_flow_run',
  {
    message0: '⤷ %1',
    args0: [
      {
        type: 'field_input',
        name: 'FLOW_NAME',
        text: ''
      }
    ],
    output: null,
    tooltip: 'Runs a sub flow',
    helpUrl: 'http://sirenia.eu/product/configuration-interface/documentation/flows/#run-flow',
    colour: Util.colors.flow,
    mutator: 'sirenia_api_flow_arg_mutator'
  },
  function(block) {
    const flowName = block.getFieldValue('FLOW_NAME')
    const argPropLines = _.compact(
      this.args.map((arg, idx) => {
        const argValCode = Blockly.JavaScript.valueToCode(
          block,
          `ARG${idx}`,
          Blockly.JavaScript.ORDER_ATOMIC
        )
        return argValCode ? `${arg.name}: ${argValCode}` : null
      })
    )
    const argCode = argPropLines.length > 0 ? `{ ${argPropLines.join(', ')} }` : ''
    const code = `Flow.run('${flowName}'${argCode ? ', ' : ''}${argCode})`
    return [code, Blockly.JavaScript.ORDER_ATOMIC]
  },
  function() {
    this.setEditable(false)
  }
)

defineBlock(
  'sirenia_api_flow_input',
  {
    message0: 'flow input %1',
    args0: [
      {
        type: 'field_input',
        name: 'INPUT_NAME',
        text: ''
      }
    ],
    output: null,
    tooltip: 'An input to this flow',
    helpUrl: 'http://sirenia.eu/product/configuration-interface/documentation/flows/#run-flow',
    mutator: 'sirenia_output_type_mutator'
  },
  function(block) {
    const nameVal = block.getFieldValue('INPUT_NAME')
    const code = `Inputs[${nameVal ? `'${nameVal}'` : 'null'}]`
    return [code, Blockly.JavaScript.ORDER_ATOMIC]
  },
  function() {
    this.setEditable(false)
  }
)

defineBlock(
  'sirenia_api_flow_include',
  {
    message0: 'imported module %1',
    args0: [
      {
        type: 'field_input',
        name: 'MODULE_NAME',
        text: ''
      }
    ],
    output: null,
    tooltip: 'Includes functionality from a module flow',
    helpUrl:
      'http://sirenia.eu/product/configuration-interface/documentation/flows/#shared-functionality',
    mutator: 'sirenia_not_editable_mutator',
    colour: Util.colors.objects
  },
  function(block) {
    var moduleName = `'${block.getFieldValue('MODULE_NAME') || ''}'`
    const code = `Flow.include(${moduleName})`
    return [code, Blockly.JavaScript.ORDER_ATOMIC]
  }
)

// NOTE: These dynamic block generators are currently unused, but will be useful to invoke from BlocklyEditor init
// Support will need to be added to BlocklifyApiInterceptors for generating these dynamic blocks if the flows they represent still exist.
function getDynamicFlowBlockId(flowId) {
  return 'sirenia_api_autoflow_' + flowId.toLowerCase().replace(/ /g, '')
}

function getDynamicInputBlockId(inputName) {
  return 'sirenia_api_autoinput_' + inputName.toLowerCase().replace(/ /g, '')
}

function defineRunFlowBlock(flowId, flowName, inputs, outputs) {
  // Insert empty dummy input to get a line break for improved block readability
  const blockArgs = [{ type: 'input_dummy' }].concat(
    inputs.map(input => {
      return {
        type: 'input_value',
        name: input.name,
        align: 'RIGHT',
        check: mapSireniaPrimitiveToBlocklyPrimitive(input.type)
      }
    })
  )
  // Generate string like '<flowName> arg1 %1 arg2 %2' which references all inputs
  const message =
    `⤷ ${flowName} ` +
    blockArgs.map((blockArg, index) => `${blockArg.name || ''}%${index + 1}`).join(' ')
  const blockConf = {
    message0: message,
    args0: blockArgs,
    colour: Util.colors.flow,
    tooltip: `Runs the flow ${flowName}`,
    helpUrl: 'http://sirenia.eu/product/configuration-interface/documentation/flows/#run-flow'
  }

  const blockIsValueInput = outputs && outputs.length > 0
  if (blockIsValueInput) {
    blockConf.output = null
  } else {
    blockConf.previousStatement = null
    blockConf.nextStatement = null
  }

  const codeGenerator = function(block) {
    const argObjPropsString = inputs
      .map(input => {
        const argValueCode =
          Blockly.JavaScript.valueToCode(block, input.name, Blockly.JavaScript.ORDER_ATOMIC) ||
          'null'
        return `${input.name}: ${argValueCode}`
      })
      .join(', ')
    const argObjString = argObjPropsString ? `{ ${argObjPropsString} }` : '{}'
    const code = `Flow.run("${flowName}", ${argObjString})${!blockIsValueInput ? ';\n' : ''}`
    // Different return value formats depending on the type of block
    if (blockIsValueInput) {
      return [code, Blockly.JavaScript.ORDER_ATOMIC]
    } else {
      return code
    }
  }

  const blockId = getDynamicFlowBlockId(flowId)

  defineBlock(blockId, blockConf, codeGenerator)
  return blockId
}

function defineFlowInputBlock(name, type) {
  const displayedType =
    type === 'string' || type === 'number' || type === 'boolean' ? type : 'custom'
  const blockConf = {
    message0: `flow input ${name}`,
    tooltip: `flow input of ${displayedType} type`,
    output: mapSireniaPrimitiveToBlocklyPrimitive(type)
  }

  const codeGenerator = function(block) {
    const code = `Inputs['${name}']`
    return [code, Blockly.JavaScript.ORDER_ATOMIC]
  }

  const blockId = getDynamicInputBlockId(name)
  defineBlock(blockId, blockConf, codeGenerator)

  return blockId
}

export default {
  defineRunFlowBlock,
  defineFlowInputBlock,
  getDynamicFlowBlockId,
  getDynamicInputBlockId
}
